import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Users, 
  Package, 
  Gavel, 
  Handshake, 
  TrendingUp, 
  TrendingDown,
  UserPlus,
  ListPlus,
  Clock,
  ShieldAlert,
  Eye,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [visitStats, setVisitStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, visitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/visits/stats`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setVisitStats(visitsRes.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const statCards = [
    { 
      label: 'Total Users', 
      value: stats?.totals?.users || 0, 
      icon: Users, 
      color: 'bg-blue-500',
      trend: stats?.last_7_days?.new_users || 0,
      trendLabel: 'new this week'
    },
    { 
      label: 'Total Listings', 
      value: stats?.totals?.listings || 0, 
      icon: Package, 
      color: 'bg-emerald-500',
      trend: stats?.last_7_days?.new_listings || 0,
      trendLabel: 'new this week'
    },
    { 
      label: 'Total Bids', 
      value: stats?.totals?.bids || 0, 
      icon: Gavel, 
      color: 'bg-amber-500',
      trend: stats?.last_7_days?.new_bids || 0,
      trendLabel: 'new this week'
    },
    { 
      label: 'Total Trades', 
      value: stats?.totals?.trades || 0, 
      icon: Handshake, 
      color: 'bg-violet-500',
      trend: stats?.active?.completed_trades || 0,
      trendLabel: 'completed'
    },
  ];

  const activeStats = [
    { label: 'Live Listings', value: stats?.active?.live_listings || 0, icon: Package, color: 'text-emerald-400' },
    { label: 'Pending Bids', value: stats?.active?.pending_bids || 0, icon: Clock, color: 'text-amber-400' },
    { label: 'Blocked Users', value: stats?.active?.blocked_users || 0, icon: ShieldAlert, color: 'text-red-400' },
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Admin Dashboard
        </h1>
        <p className="text-slate-400">Overview of Bid2Barter platform activity</p>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => (
          <div 
            key={stat.label}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
            data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center gap-1 text-emerald-400 text-sm">
                <TrendingUp className="w-4 h-4" />
                <span>+{stat.trend}</span>
              </div>
            </div>
            <h3 className="text-3xl font-bold text-white mb-1">{stat.value.toLocaleString()}</h3>
            <p className="text-slate-400 text-sm">{stat.label}</p>
            <p className="text-xs text-slate-500 mt-1">{stat.trend} {stat.trendLabel}</p>
          </div>
        ))}
      </div>

      {/* Active Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {activeStats.map((stat) => (
          <div 
            key={stat.label}
            className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 flex items-center gap-4"
          >
            <stat.icon className={`w-8 h-8 ${stat.color}`} />
            <div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-sm text-slate-400">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Visit Analytics */}
      {visitStats && (
        <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 mb-8" data-testid="visit-stats-widget">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Website Traffic
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-700/50 rounded-xl p-4">
              <div className="flex items-center gap-2 text-cyan-300 mb-1">
                <Eye className="w-4 h-4" />
                <p className="text-xs uppercase">Total Visits</p>
              </div>
              <p className="text-2xl font-bold text-white">{(visitStats.total_visits || 0).toLocaleString()}</p>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-xs uppercase text-cyan-300 mb-1">Today</p>
              <p className="text-2xl font-bold text-white">{(visitStats.visits_today || 0).toLocaleString()}</p>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-xs uppercase text-cyan-300 mb-1">Last 7 Days</p>
              <p className="text-2xl font-bold text-white">{(visitStats.visits_7d || 0).toLocaleString()}</p>
              <p className="text-xs text-slate-500 mt-1">{(visitStats.unique_visitors_7d || 0).toLocaleString()} unique</p>
            </div>
            <div className="bg-slate-700/50 rounded-xl p-4">
              <p className="text-xs uppercase text-cyan-300 mb-1">Unique Visitors</p>
              <p className="text-2xl font-bold text-white">{(visitStats.unique_visitors_total || 0).toLocaleString()}</p>
            </div>
          </div>
          {visitStats.top_pages && visitStats.top_pages.length > 0 && (
            <div className="mt-5">
              <p className="text-sm text-slate-400 mb-2 font-medium">Top Pages</p>
              <div className="space-y-1">
                {visitStats.top_pages.slice(0, 5).map((page) => (
                  <div key={page.path} className="flex items-center justify-between text-sm bg-slate-700/30 rounded-lg px-3 py-2">
                    <span className="text-slate-300 truncate flex-1 font-mono">{page.path}</span>
                    <span className="text-white font-semibold ml-3">{page.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Distribution */}
      <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
        <h2 className="text-xl font-bold text-white mb-4">Category Distribution</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {stats?.category_distribution?.map((cat, index) => (
            <div 
              key={index}
              className="bg-slate-700/50 rounded-xl p-4 text-center"
            >
              <p className="text-2xl font-bold text-white">{cat.count}</p>
              <p className="text-sm text-slate-400">{cat._id || 'Unknown'}</p>
            </div>
          ))}
          {(!stats?.category_distribution || stats.category_distribution.length === 0) && (
            <p className="text-slate-500 col-span-5 text-center py-4">No category data available</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-2">User Management</h3>
          <p className="text-indigo-100 mb-4">View, search, and manage platform users</p>
          <a 
            href="/admin/users"
            className="inline-flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
          >
            <Users className="w-4 h-4" />
            Manage Users
          </a>
        </div>
        <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-6">
          <h3 className="text-xl font-bold text-white mb-2">Listing Management</h3>
          <p className="text-emerald-100 mb-4">View, remove, and restore platform listings</p>
          <a 
            href="/admin/listings"
            className="inline-flex items-center gap-2 bg-white text-emerald-600 px-4 py-2 rounded-xl font-semibold hover:bg-emerald-50 transition-colors"
          >
            <Package className="w-4 h-4" />
            Manage Listings
          </a>
        </div>
      </div>
    </div>
  );
}
